/**
 * CapabilityLevelDescriptions
 *
 * Displays the full MeasureText description for all 6 maturity levels of a
 * capability so assessors can read calibrated scoring criteria before committing
 * to a score.  The currently selected level is visually highlighted.
 *
 * The panel is collapsed by default to keep the form compact; assessors expand
 * it on demand via a toggle button.
 */

import { useState } from 'react';
import type { MaturityLevel } from '../../graphql/types';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import './CapabilityLevelDescriptions.css';

export interface CapabilityLevelDescriptionsProps {
  /** All level definitions for this capability (typically 6). */
  levels: MaturityLevel[];
  /** Currently selected maturity level (1–6); highlights that row. */
  selectedLevel: number;
  /** Optional: start expanded. Defaults to collapsed. */
  defaultExpanded?: boolean;
}

export function CapabilityLevelDescriptions({
  levels,
  selectedLevel,
  defaultExpanded = false,
}: CapabilityLevelDescriptionsProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (levels.length === 0) {
    return null;
  }

  const ForgeIcon = 'forge-icon' as React.ElementType;

  const handleToggle = (): void => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className="level-descriptions" data-testid="level-descriptions">
      <button
        type="button"
        className="level-descriptions__toggle"
        aria-expanded={expanded}
        aria-controls="level-descriptions-panel"
        onClick={handleToggle}
      >
        <ForgeIcon
          name={expanded ? 'expand_less' : 'expand_more'}
          className="level-descriptions__toggle-icon"
          aria-hidden="true"
        />
        <span>Level Descriptions</span>
      </button>

      {expanded && (
        <ol
          id="level-descriptions-panel"
          className="level-descriptions__list"
          aria-label="Level descriptions"
        >
          {levels.map((levelDef) => {
            const isSelected = levelDef.level === selectedLevel;
            return (
              <li
                key={levelDef.level}
                className={`level-descriptions__item${isSelected ? ' level-descriptions__item--selected' : ''}`}
                aria-current={isSelected ? 'true' : undefined}
              >
                <span className="level-descriptions__level-badge">
                  {isSelected && (
                    <ForgeIcon
                      name="arrow_right"
                      className="level-descriptions__selected-icon"
                      aria-label="selected level"
                    />
                  )}
                  L{levelDef.level} — {levelDef.name}
                </span>
                <span className="level-descriptions__text">{levelDef.description}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
